import { useState, useEffect } from "react";
import { Button } from "../components/ui/button";
import { Copy, Check, Palette, Ruler, Sparkles, Type, Layers, MousePointerClick, ToggleRight } from "lucide-react";
import { Switch } from "../components/ui/switch";
import { toast } from "sonner";

/*
  VeriHandi Design System — developer reference.
  Single source of truth: /app/frontend/src/index.css (tokens) + tailwind.config.js (brand scale).
  Every swatch is click-to-copy so you can paste directly into a page.
*/

const BRAND_SCALE = [
  ["50",  "#F2F7F5"],
  ["100", "#E0ECE7"],
  ["200", "#BFD9CE"],
  ["300", "#9EC6B5"],
  ["400", "#6FA88F"],
  ["500", "#3F7A68"],
  ["600", "#2F5F51"],
  ["700", "#22473D"],
  ["800", "#163029"],
  ["900", "#0B1815"],
];

const WARM_SCALE = [
  ["50",  "#FBEBE1"],
  ["500", "#C46E4E"],
  ["600", "#A85A3E"],
];

const SEMANTIC_TOKENS = [
  { name: "--primary",       use: "Button fill (same in both themes)" },
  { name: "--primary-hover", use: "Primary hover state" },
  { name: "--primary-ink",   use: "Primary text/icon on surfaces" },
  { name: "--primary-soft",  use: "Tinted brand backgrounds" },
  { name: "--accent",        use: "Warm accent (flame, skip, favourite)" },
  { name: "--accent-hover",  use: "Accent hover state" },
  { name: "--accent-ink",    use: "Accent text/icon on surfaces" },
  { name: "--accent-soft",   use: "Tinted warm backgrounds" },
  { name: "--success",       use: "Positive / taken state" },
  { name: "--success-bg",    use: "Success tinted bg" },
  { name: "--warning",       use: "Due-soon / pending" },
  { name: "--warning-bg",    use: "Warning tinted bg" },
  { name: "--error",         use: "Destructive / error" },
];

const SURFACE_TOKENS = [
  { name: "--bg",            use: "App background" },
  { name: "--surface",       use: "Card / panel" },
  { name: "--surface-2",     use: "Subtle nested surface" },
  { name: "--border-subtle", use: "Quiet divider / border" },
  { name: "--text-main",     use: "Primary text" },
  { name: "--text-muted",    use: "Secondary text / captions" },
];

const SWITCH_TOKENS = [
  { name: "--switch-track-on",         use: "Track when ON (brand-green by default)" },
  { name: "--switch-track-on-border",  use: "1-2px border on the ON track" },
  { name: "--switch-track-off",        use: "Track when OFF (warm gray)" },
  { name: "--switch-track-off-border", use: "Border on the OFF track" },
  { name: "--switch-thumb",            use: "Round handle color" },
  { name: "--switch-thumb-ring",       use: "Subtle outline around the thumb" },
  { name: "--switch-label-on",         use: "ON text inside the track" },
  { name: "--switch-label-off",        use: "OFF text inside the track" },
];

const BUTTON_TOKENS = [
  { name: "--button-outline-bg",        use: "Outline button base background" },
  { name: "--button-outline-bg-hover",  use: "Outline button hover background" },
  { name: "--button-outline-text",      use: "Outline button text color (must contrast both backgrounds)" },
  { name: "--button-outline-border",    use: "Outline button border" },
];

const RADII = [
  ["--r-sm", "8px",  "Pills, chips, tight elements"],
  ["--r-md", "12px", "Inputs, small cards"],
  ["--r-lg", "16px", "Cards, modals"],
  ["--r-xl", "24px", "Hero sections, feature cards"],
];

const SHADOWS = [
  ["--sh-sm", "Subtle (1px edge)"],
  ["--sh-md", "Default card lift"],
  ["--sh-lg", "Elevated modal / dropdown"],
];

const MOTION = [
  ["--m-fast", "150ms", "Hover states, toggles"],
  ["--m-base", "250ms", "Panels, tabs"],
  ["--m-slow", "350ms", "Page transitions, reveals"],
];

const TYPE_SCALE = [
  { label: "H1",      className: "text-4xl sm:text-5xl lg:text-6xl font-display font-black tracking-tighter", sample: "A quiet set of tools." },
  { label: "H2",      className: "text-2xl font-display font-bold tracking-tight",                             sample: "Prescription Manager" },
  { label: "H3",      className: "text-xl font-display font-bold tracking-tight",                              sample: "Section heading" },
  { label: "Body",    className: "text-base",                                                                  sample: "The quiet tools of daily life." },
  { label: "Small",   className: "text-sm",                                                                    sample: "Supporting detail or helper text." },
  { label: "Caption", className: "text-xs text-[color:var(--text-muted)]",                                     sample: "Timestamps, metadata, small labels." },
  { label: "label-caps", className: "label-caps",                                                              sample: "SECTION LABEL" },
];

function useResolvedVar(name) {
  const [val, setVal] = useState("");
  useEffect(() => {
    try {
      const v = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
      setVal(v);
    } catch { setVal(""); }
  }, [name]);
  return val;
}

function CopyChip({ label }) {
  const [copied, setCopied] = useState(false);
  function copy() {
    navigator.clipboard.writeText(label).then(() => {
      setCopied(true);
      toast.success(`Copied: ${label}`);
      setTimeout(() => setCopied(false), 1200);
    }).catch(() => toast.error("Clipboard blocked"));
  }
  return (
    <button
      onClick={copy}
      data-testid={`copy-${label}`}
      className="inline-flex items-center gap-1.5 text-[11px] font-mono px-2 py-1 rounded-full border border-[color:var(--border-subtle)] hover:border-[color:var(--primary)] hover:bg-[color:var(--surface-2)] transition-colors"
    >
      {copied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
      <span>{label}</span>
    </button>
  );
}

function SemanticSwatch({ name, use }) {
  const resolved = useResolvedVar(name);
  const sample = `var(${name})`;
  return (
    <div className="card-tactile rounded-xl p-3 flex items-center gap-3">
      <div
        className="h-12 w-12 rounded-lg shrink-0 border border-[color:var(--border-subtle)]"
        style={{ background: sample }}
        aria-label={name}
      />
      <div className="min-w-0 flex-1">
        <div className="font-mono text-xs truncate">{name}</div>
        <div className="text-[11px] text-[color:var(--text-muted)] truncate">{use}</div>
        <div className="mt-1 flex items-center gap-1.5">
          <span className="text-[10px] font-mono text-[color:var(--text-muted)]">{resolved || "—"}</span>
        </div>
      </div>
      <CopyChip label={sample} />
    </div>
  );
}

function ScaleSwatch({ step, hex, name }) {
  const [copied, setCopied] = useState(false);
  const textColor = parseInt(hex.slice(1, 3), 16) + parseInt(hex.slice(3, 5), 16) + parseInt(hex.slice(5, 7), 16) > 380 ? "#0B1815" : "#F2F7F5";
  function copy() {
    navigator.clipboard.writeText(hex).then(() => {
      setCopied(true);
      toast.success(`Copied ${name}.${step}: ${hex}`);
      setTimeout(() => setCopied(false), 1200);
    });
  }
  return (
    <button
      onClick={copy}
      data-testid={`scale-${name}-${step}`}
      className="group relative h-20 rounded-xl overflow-hidden border border-[color:var(--border-subtle)] transition-transform hover:scale-[1.02]"
      style={{ background: hex }}
    >
      <div className="absolute inset-0 flex flex-col justify-between p-2 text-left" style={{ color: textColor }}>
        <div className="font-mono text-xs font-bold">{step}</div>
        <div className="font-mono text-[10px] opacity-80">{hex}</div>
      </div>
      <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
        {copied ? <Check className="h-3.5 w-3.5" style={{ color: textColor }} /> : <Copy className="h-3.5 w-3.5" style={{ color: textColor }} />}
      </div>
    </button>
  );
}

function SectionHeader({ icon: Icon, title, desc }) {
  return (
    <div className="flex items-start gap-3 mb-4">
      <div className="h-9 w-9 rounded-lg bg-[color:var(--primary-soft)] flex items-center justify-center shrink-0">
        <Icon className="h-4 w-4 text-[color:var(--primary-ink)]" />
      </div>
      <div>
        <h2 className="font-display font-bold text-xl tracking-tight">{title}</h2>
        {desc && <p className="text-sm text-[color:var(--text-muted)] mt-0.5">{desc}</p>}
      </div>
    </div>
  );
}

export default function TokensPage() {
  return (
    <div className="animate-fade-up max-w-5xl">
      <div className="mb-8">
        <span className="label-caps">Developer · v1.19</span>
        <h1 className="mt-2 text-3xl sm:text-4xl lg:text-5xl font-display font-black tracking-tighter">Design system.</h1>
        <p className="mt-2 text-[color:var(--text-muted)]">
          Every token VeriHandi ships with. Click any swatch to copy. Switch themes in the top-right to see light/dark values in place.
        </p>
        <div className="mt-3 text-xs inline-flex items-center gap-2 rounded-full border border-[color:var(--border-subtle)] px-3 py-1.5 bg-[color:var(--surface-2)]">
          <span className="font-mono text-[color:var(--text-muted)]">source · /src/index.css · tailwind.config.js</span>
        </div>
      </div>

      {/* 1. Brand scale */}
      <section className="mb-10" data-testid="tokens-brand">
        <SectionHeader icon={Palette} title="Brand scale" desc="Green primary from brand.50 (tint) to brand.900 (deep ink). Use in Tailwind as `bg-brand-500`, `text-brand-600` etc." />
        <div className="grid grid-cols-5 sm:grid-cols-10 gap-2">
          {BRAND_SCALE.map(([step, hex]) => <ScaleSwatch key={step} step={step} hex={hex} name="brand" />)}
        </div>
      </section>

      {/* 2. Warm scale */}
      <section className="mb-10" data-testid="tokens-warm">
        <SectionHeader icon={Palette} title="Warm accent" desc="Used for favourites, skips, flames. Available as `bg-warm-500`, `text-warm-600`." />
        <div className="grid grid-cols-3 gap-2 max-w-sm">
          {WARM_SCALE.map(([step, hex]) => <ScaleSwatch key={step} step={step} hex={hex} name="warm" />)}
        </div>
      </section>

      {/* 3. Semantic tokens */}
      <section className="mb-10" data-testid="tokens-semantic">
        <SectionHeader icon={Layers} title="Semantic tokens" desc="Theme-aware CSS variables. These are the ones you should reach for in components." />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {SEMANTIC_TOKENS.map(t => <SemanticSwatch key={t.name} name={t.name} use={t.use} />)}
        </div>
      </section>

      {/* 4. Surface & text */}
      <section className="mb-10" data-testid="tokens-surface">
        <SectionHeader icon={Layers} title="Surface & text" desc="Canvas, card, and text colors." />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {SURFACE_TOKENS.map(t => <SemanticSwatch key={t.name} name={t.name} use={t.use} />)}
        </div>
      </section>

      {/* 4b. Switch / toggle tokens */}
      <section className="mb-10" data-testid="tokens-switch">
        <SectionHeader
          icon={ToggleRight}
          title="Switch / toggle"
          desc="Single source of truth for every Switch component in the app. Edit a variable in /index.css to retheme every toggle at once."
        />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="md:col-span-1 card-tactile rounded-xl p-6 flex flex-col items-center justify-center gap-4">
            <div className="label-caps">Live preview</div>
            <SwitchDemo />
            <div className="text-[11px] text-center" style={{ color: "var(--text-muted)" }}>
              Tap to flip — both states use the same tokens.
            </div>
          </div>
          <div className="md:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-3">
            {SWITCH_TOKENS.map(t => <SemanticSwatch key={t.name} name={t.name} use={t.use} />)}
          </div>
        </div>
      </section>

      {/* 4c. Button tokens (outline variant — historic source of contrast bugs) */}
      <section className="mb-10" data-testid="tokens-button">
        <SectionHeader
          icon={MousePointerClick}
          title="Button — outline variant"
          desc="Outline buttons used to render with white-on-light text in dark or hover states. These tokens pin the colors so the variant is always readable."
        />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="md:col-span-1 card-tactile rounded-xl p-6 flex flex-col items-center justify-center gap-4">
            <div className="label-caps">Live preview</div>
            <Button variant="outline" size="sm">Default</Button>
            <Button variant="outline" size="sm" className="bg-[color:var(--button-outline-bg-hover)]">Hover</Button>
            <Button variant="outline" size="sm" disabled>Disabled</Button>
          </div>
          <div className="md:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-3">
            {BUTTON_TOKENS.map(t => <SemanticSwatch key={t.name} name={t.name} use={t.use} />)}
          </div>
        </div>
      </section>

      {/* 5. Radii */}
      <section className="mb-10" data-testid="tokens-radii">
        <SectionHeader icon={Ruler} title="Radii" desc="8px system. Keep radius consistent within a hierarchy." />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {RADII.map(([name, value, use]) => (
            <div key={name} className="card-tactile p-4 flex flex-col items-start gap-2" style={{ borderRadius: `var(${name})` }}>
              <div className="h-10 w-full bg-[color:var(--primary-soft)]" style={{ borderRadius: `var(${name})` }} />
              <div className="font-mono text-xs">{name}</div>
              <div className="text-[11px] text-[color:var(--text-muted)]">{value} · {use}</div>
              <CopyChip label={`var(${name})`} />
            </div>
          ))}
        </div>
      </section>

      {/* 6. Shadows */}
      <section className="mb-10" data-testid="tokens-shadows">
        <SectionHeader icon={Sparkles} title="Shadows" desc="Subtle by design. Loud shadows = noisy UI." />
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {SHADOWS.map(([name, use]) => (
            <div key={name} className="rounded-xl p-5 bg-[color:var(--surface)] border border-[color:var(--border-subtle)]" style={{ boxShadow: `var(${name})` }}>
              <div className="font-mono text-xs mb-1">{name}</div>
              <div className="text-[11px] text-[color:var(--text-muted)] mb-3">{use}</div>
              <CopyChip label={`var(${name})`} />
            </div>
          ))}
        </div>
      </section>

      {/* 7. Motion */}
      <section className="mb-10" data-testid="tokens-motion">
        <SectionHeader icon={MousePointerClick} title="Motion" desc="Hover each tile — transition speed is applied live." />
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {MOTION.map(([name, value, use]) => (
            <div
              key={name}
              className="card-tactile rounded-xl p-5 cursor-pointer hover:bg-[color:var(--primary-soft)]"
              style={{ transition: `background-color var(${name})` }}
            >
              <div className="font-mono text-xs mb-1">{name}</div>
              <div className="text-[11px] text-[color:var(--text-muted)] mb-3">{value} · {use}</div>
              <CopyChip label={`var(${name})`} />
            </div>
          ))}
        </div>
      </section>

      {/* 8. Typography */}
      <section className="mb-10" data-testid="tokens-type">
        <SectionHeader icon={Type} title="Typography" desc="Manrope display + IBM Plex Sans body. Keep the scale tight." />
        <div className="card-tactile rounded-xl p-6 space-y-4">
          {TYPE_SCALE.map(t => (
            <div key={t.label} className="flex items-baseline gap-6 border-b border-[color:var(--border-subtle)] pb-3 last:border-0 last:pb-0">
              <div className="font-mono text-[10px] text-[color:var(--text-muted)] w-20 shrink-0 uppercase tracking-widest">{t.label}</div>
              <div className={t.className + " flex-1"}>{t.sample}</div>
            </div>
          ))}
        </div>
      </section>

      {/* 9. Component anatomy */}
      <section className="mb-10" data-testid="tokens-components">
        <SectionHeader icon={Sparkles} title="Components in context" desc="Live examples using the tokens above. These should always look right in both themes." />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Buttons */}
          <div className="card-tactile rounded-xl p-6">
            <div className="label-caps mb-4">Buttons</div>
            <div className="flex flex-wrap items-center gap-2">
              <Button className="bg-[color:var(--primary)] hover:bg-[color:var(--primary-hover)] text-white">Primary</Button>
              <Button variant="outline" className="border-[color:var(--border-subtle)]">Outline</Button>
              <Button variant="ghost" className="text-[color:var(--text-muted)] hover:text-[color:var(--text-main)] hover:bg-[color:var(--surface-2)]">Ghost</Button>
              <Button disabled className="bg-[color:var(--primary)] text-white opacity-50">Disabled</Button>
            </div>
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <Button className="bg-[color:var(--accent)] hover:bg-[color:var(--accent-hover)] text-white">Accent</Button>
              <Button className="bg-[color:var(--text-main)] text-[color:var(--surface)] border border-[color:var(--text-main)] hover:opacity-90">Inverse</Button>
            </div>
          </div>

          {/* Pills */}
          <div className="card-tactile rounded-xl p-6">
            <div className="label-caps mb-4">Pills & states</div>
            <div className="flex flex-wrap items-center gap-2">
              <span className="dose-pill taken">Taken</span>
              <span className="dose-pill auto-taken">Auto</span>
              <span className="dose-pill pending">Pending</span>
              <span className="dose-pill skipped">Skipped</span>
              <span className="dose-pill due-soon">Due soon</span>
            </div>
          </div>

          {/* Card */}
          <div className="card-tactile rounded-xl p-6">
            <div className="label-caps mb-4">Card</div>
            <div className="card-tactile rounded-xl p-5">
              <div className="flex items-center gap-3 mb-2">
                <div className="h-9 w-9 rounded-lg bg-[color:var(--primary-soft)] flex items-center justify-center">
                  <Sparkles className="h-4 w-4 text-[color:var(--primary-ink)]" />
                </div>
                <div>
                  <div className="font-display font-bold">Card title</div>
                  <div className="text-xs text-[color:var(--text-muted)]">Supporting metadata</div>
                </div>
              </div>
              <p className="text-sm text-[color:var(--text-muted)]">Nested cards inherit tokens and look correct in both themes with zero extra effort.</p>
            </div>
          </div>

          {/* Inputs */}
          <div className="card-tactile rounded-xl p-6">
            <div className="label-caps mb-4">Input</div>
            <div className="space-y-2">
              <input
                type="text"
                placeholder="Type something…"
                className="w-full px-3 py-2 rounded-md bg-[color:var(--surface)] border border-[color:var(--border-subtle)] text-[color:var(--text-main)] placeholder:text-[color:var(--text-muted)] focus:outline-none focus:border-[color:var(--primary)]"
              />
              <div className="text-[11px] text-[color:var(--text-muted)]">Placeholder, border, and focus ring all use tokens.</div>
            </div>
          </div>
        </div>
      </section>

      {/* 10. Usage cheatsheet */}
      <section className="mb-10" data-testid="tokens-usage">
        <SectionHeader icon={Copy} title="Usage cheatsheet" desc="Copy-paste snippets. Prefer these over raw hex values." />
        <div className="card-tactile rounded-xl p-6 font-mono text-xs leading-6 overflow-x-auto">
          <div><span className="text-[color:var(--text-muted)]">// Button (primary)</span></div>
          <div>bg-[color:var(--primary)] hover:bg-[color:var(--primary-hover)] text-white</div>
          <div className="mt-2"><span className="text-[color:var(--text-muted)]">// Text on light/dark surfaces</span></div>
          <div>text-[color:var(--primary-ink)] · text-[color:var(--accent-ink)]</div>
          <div className="mt-2"><span className="text-[color:var(--text-muted)]">// Tinted bg</span></div>
          <div>bg-[color:var(--primary-soft)] · bg-[color:var(--accent-soft)] · bg-[color:var(--warning-bg)]</div>
          <div className="mt-2"><span className="text-[color:var(--text-muted)]">// Border / divider</span></div>
          <div>border-[color:var(--border-subtle)] · divide-[color:var(--border-subtle)]</div>
          <div className="mt-2"><span className="text-[color:var(--text-muted)]">// Tailwind brand scale</span></div>
          <div>bg-brand-500 · text-brand-600 · bg-warm-50</div>
        </div>
      </section>

      <div className="text-xs text-[color:var(--text-muted)] text-center pt-6 pb-10">
        Rule of thumb · <b>1 primary, 1 accent, neutrals do most of the work.</b>
      </div>
    </div>
  );
}

function SwitchDemo() {
  const [a, setA] = useState(true);
  const [b, setB] = useState(false);
  return (
    <div className="flex flex-col items-center gap-3">
      <div className="flex items-center gap-3">
        <span className="text-xs font-semibold" style={{ color: "var(--text-muted)" }}>OFF →</span>
        <Switch checked={b} onCheckedChange={setB} aria-label="Demo switch off" />
      </div>
      <div className="flex items-center gap-3">
        <span className="text-xs font-semibold" style={{ color: "var(--text-muted)" }}>ON →</span>
        <Switch checked={a} onCheckedChange={setA} aria-label="Demo switch on" />
      </div>
    </div>
  );
}
